<template>
  <div class="play-platform-modal" @click="$emit('close')">
    <div class="play-platform-content" @click.stop>
      <div class="play-platform-header">
        <h3>选择播放平台</h3>
        <button class="modal-close-btn" @click="$emit('close')">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
      <div class="play-platform-body">
        <p class="play-description">请选择您偏好的在线播放平台观看《{{ documentaryTitle }}》</p>
        
        <!-- 有可用视频URL时显示实际平台链接 -->
        <div v-if="hasVideoURLs" class="platform-grid">
          <a 
            v-for="url in videoURLs" 
            :key="url.video_url_id"
            :href="url.video_url" 
            target="_blank" 
            class="platform-link"
          >
            <div class="platform-item">
              <div class="platform-icon" :style="getPlatformStyle(url.platform)">
                <i class="fas fa-play"></i>
              </div>
              <div class="platform-info">
                <h4>{{ url.platform?.name || '未知平台' }}</h4>
                <p>{{ url.platform?.description || '点击观看' }}</p>
              </div>
            </div>
          </a>
        </div>
        
        <!-- 没有视频URL时显示搜索链接 -->
        <div v-else class="platform-grid">
          <a :href="getPlatformSearchUrl('bilibili')" target="_blank" class="platform-link">
            <div class="platform-item">
              <div class="platform-icon bilibili-icon">
                <i class="fas fa-play"></i>
              </div>
              <div class="platform-info">
                <h4>哔哩哔哩</h4>
                <p>免费观看，弹幕互动</p>
              </div>
            </div>
          </a>
          <a :href="getPlatformSearchUrl('iqiyi')" target="_blank" class="platform-link">
            <div class="platform-item">
              <div class="platform-icon iqiyi-icon">
                <i class="fas fa-play"></i>
              </div>
              <div class="platform-info">
                <h4>爱奇艺</h4>
                <p>高清画质，会员专享</p>
              </div>
            </div>
          </a>
          <a :href="getPlatformSearchUrl('tencent')" target="_blank" class="platform-link">
            <div class="platform-item">
              <div class="platform-icon tencent-icon">
                <i class="fas fa-play"></i>
              </div>
              <div class="platform-info">
                <h4>腾讯视频</h4>
                <p>海量资源，独家内容</p>
              </div>
            </div>
          </a>
          <a :href="getPlatformSearchUrl('youku')" target="_blank" class="platform-link">
            <div class="platform-item">
              <div class="platform-icon youku-icon">
                <i class="fas fa-play"></i>
              </div>
              <div class="platform-info">
                <h4>优酷</h4>
                <p>经典纪录片，品质保证</p>
              </div>
            </div>
          </a>
          <a :href="getPlatformSearchUrl('mgtv')" target="_blank" class="platform-link">
            <div class="platform-item">
              <div class="platform-icon mgtv-icon">
                <i class="fas fa-play"></i>
              </div>
              <div class="platform-info">
                <h4>芒果TV</h4>
                <p>综艺纪录片，特色内容</p>
              </div>
            </div>
          </a>
          <a :href="getPlatformSearchUrl('cctv')" target="_blank" class="platform-link">
            <div class="platform-item">
              <div class="platform-icon cctv-icon">
                <i class="fas fa-play"></i>
              </div>
              <div class="platform-info">
                <h4>CCTV节目官网</h4>
                <p>官方正版，权威发布</p>
              </div>
            </div>
          </a>
        </div>
        
        <div class="play-tips">
          <p v-if="hasVideoURLs">
            <i class="fas fa-info-circle"></i> 提示：点击平台即可跳转观看
          </p>
          <p v-else>
            <i class="fas fa-info-circle"></i> 提示：各平台资源可能有所不同，建议多平台搜索
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { DocumentaryVideoURL } from '@/types'

interface Props {
  documentaryTitle?: string
  videoURLs?: DocumentaryVideoURL[]
}

const props = withDefaults(defineProps<Props>(), {
  documentaryTitle: '',
  videoURLs: () => []
})

defineEmits<{
  close: []
}>()

// 是否有可用的视频URL
const hasVideoURLs = computed(() => {
  return props.videoURLs && props.videoURLs.length > 0
})

// 获取平台样式
const getPlatformStyle = (platform?: { color?: string }) => {
  if (platform?.color) {
    return { background: platform.color }
  }
  return { background: 'linear-gradient(135deg, #667eea, #764ba2)' }
}

// 获取平台搜索URL
const getPlatformSearchUrl = (platform: string): string => {
  const documentaryTitle = props.documentaryTitle || ''
  const encodedTitle = encodeURIComponent(documentaryTitle)
  
  const platformUrls: Record<string, string> = {
    bilibili: `https://search.bilibili.com/all?keyword=${encodedTitle}`,
    iqiyi: `https://www.iqiyi.com/search/${encodedTitle}`,
    tencent: `https://v.qq.com/search.html?keyword=${encodedTitle}`,
    youku: `https://www.youku.com/search_video/q_${encodedTitle}`,
    mgtv: `https://www.mgtv.com/search?last=1&kw=${encodedTitle}`,
    cctv: `https://search.cctv.com/search.php?qtext=${encodedTitle}&channel=program`
  }
  
  return platformUrls[platform] || platformUrls.bilibili
}
</script>

<style scoped>
.play-platform-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 20px;
}

.play-platform-content {
  background: white;
  border-radius: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.play-platform-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0 24px;
  margin-bottom: 20px;
}

.play-platform-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--dark-text);
  font-family: 'Georgia', 'Times New Roman', serif;
}

.modal-close-btn {
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-close-btn:hover {
  background: rgba(0, 0, 0, 0.2);
  transform: scale(1.1);
}

.modal-close-btn svg {
  width: 20px;
  height: 20px;
  color: #666;
}

.play-platform-body {
  padding: 0 24px 24px 24px;
}

.play-description {
  margin: 0 0 24px 0;
  font-size: 16px;
  color: var(--light-text);
  text-align: center;
  line-height: 1.6;
}

.platform-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.platform-link {
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease;
}

.platform-link:hover {
  transform: translateY(-2px);
}

.platform-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: var(--glass-bg);
  border: 2px solid var(--glass-border);
  border-radius: 12px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.platform-item:hover {
  border-color: var(--secondary-color);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.2);
  background: rgba(102, 126, 234, 0.05);
}

.platform-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
}

.bilibili-icon {
  background: linear-gradient(135deg, #00A1D6, #FB7299);
}

.iqiyi-icon {
  background: linear-gradient(135deg, #00B4D8, #0077B6);
}

.tencent-icon {
  background: linear-gradient(135deg, #FF6B6B, #4ECDC4);
}

.youku-icon {
  background: linear-gradient(135deg, #FF9F1C, #FF6B6B);
}

.mgtv-icon {
  background: linear-gradient(135deg, #FFB700, #FF6B35);
}

.cctv-icon {
  background: linear-gradient(135deg, #1A5490, #2E86AB);
}

.platform-info {
  flex: 1;
  min-width: 0;
}

.platform-info h4 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--dark-text);
}

.platform-info p {
  margin: 0;
  font-size: 13px;
  color: var(--light-text);
  line-height: 1.4;
}

.play-tips {
  text-align: center;
  padding: 16px;
  background: var(--light-bg);
  border-radius: 12px;
  margin-top: 16px;
}

.play-tips p {
  margin: 0;
  font-size: 14px;
  color: var(--light-text);
  line-height: 1.5;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.play-tips i {
  color: var(--secondary-color);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .platform-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .platform-item {
    padding: 12px;
  }
  
  .platform-icon {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
  
  .platform-info h4 {
    font-size: 14px;
  }
  
  .platform-info p {
    font-size: 12px;
  }
  
  .play-platform-content {
    margin: 10px;
    border-radius: 16px;
  }
  
  .play-platform-header {
    padding: 20px 20px 0 20px;
  }
  
  .play-platform-body {
    padding: 0 20px 20px 20px;
  }
  
  .play-description {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .platform-item {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .platform-info {
    text-align: center;
  }
}
</style>